import { Form, Select, Tooltip } from "antd";
import ProjectUserParticipantsDropdown from "components/project-participants-dropdown/project-participants-dropdown";
import SelectSearchNotFoundContent from "components/widgets/select-search-notfound-content";
import {
  CompanyParticipationType,
  EUserTypes,
  UserParticipationType
} from "constants/index";
import { useProjectParticipants } from "hooks/project-participants";
import { useEffect, useMemo, useState } from "react";
import { matchUserProjectRole } from "utils/utils";

export default function FeatureMaterialParticipants(props: {
  actions?: any;
  featureInstanceData?: any;
  disabled: boolean;
  materialHeaderMap: any;
  projectId: string;
  tokenContents: any;
}) {
  const {
    actions,
    featureInstanceData,
    disabled,
    materialHeaderMap,
    projectId,
    tokenContents
  } = props;
  const { Option } = Select;
  const [form] = Form.useForm();
  const isLoggedInUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const { projectParticipants } = useProjectParticipants(!isLoggedInUserGC);
  const selectedRCCompanyId = Form.useWatch("responsible_contractor", form);

  const [localFeatureInstanceData, setLocalFeatureInstanceData] =
    useState<any>();

  useEffect(() => {
    if (!featureInstanceData) return;
    setLocalFeatureInstanceData(structuredClone(featureInstanceData));
  }, [featureInstanceData]);

  const materialParticipants = useMemo(() => {
    const contractor =
      localFeatureInstanceData?.feature_company_participants?.find(
        (company: any) =>
          company?.company_participation_type_id ===
          CompanyParticipationType.RESPONSIBLE_CONTRACTOR
      );

    const submitter = localFeatureInstanceData?.feature_user_paticipants?.find(
      (user: any) =>
        user?.user_participation_type?.id === UserParticipationType.ASSIGNEE
    );

    const gcReviewer = localFeatureInstanceData?.feature_user_paticipants?.find(
      (user: any) =>
        user?.user_participation_type?.id ===
        UserParticipationType.GC_REPRESENTATIVE
    );

    return {
      contractor,
      submitter: submitter?.user_unregistered
        ? {
            user: {
              id: submitter.user_unregistered,
              first_name: submitter.user_unregistered
            }
          }
        : submitter,
      gcReviewer
    };
  }, [
    localFeatureInstanceData?.feature_company_participants,
    localFeatureInstanceData?.feature_user_paticipants
  ]);

  const responsibleContractorOptions = useMemo(() => {
    if (!disabled) {
      return projectParticipants?.materialTradePartners?.map((company: any) => {
        return {
          id: company.vendor_id,
          name: company.subscription_vendor.name,
          companyInviteStatus: company.project_vendors_company_status?.status,
          ...company
        };
      });
    }

    if (!materialParticipants.contractor) return [];

    const idStr = materialParticipants.contractor?.vendor_source_info
      ? materialParticipants.contractor?.vendor_source_info?.id
      : materialParticipants.contractor?.vendor_id;

    const nameStr = materialParticipants.contractor?.vendor_source_info
      ? materialParticipants.contractor?.vendor_source_info?.name
      : materialParticipants.contractor?.vendor?.name;

    const rc = [
      {
        id: idStr,
        name: nameStr,
        companyInviteStatus: ""
      }
    ];

    return rc;
  }, [
    projectParticipants.materialTradePartners,
    disabled,
    materialParticipants.contractor
  ]);

  useEffect(() => {
    if (!localFeatureInstanceData) return;

    form.setFieldsValue({
      responsible_contractor: materialParticipants.contractor?.vendor_id,
      submitter: materialParticipants.submitter?.user?.id,
      gc_representative: materialParticipants.gcReviewer?.user?.id
    });
  }, [localFeatureInstanceData, form, materialParticipants]);

  return (
    <div
      className="grow h-[250px] overflow-auto"
      data-testid="material-participants-root"
    >
      <Form
        form={form}
        layout="vertical"
        data-testid="material-participants-form"
      >
        <Form.Item
          name="responsible_contractor"
          label={materialHeaderMap?.trade_partner}
          data-testid="material-participants-responsible-contractor-item"
        >
          <Select
            allowClear
            onChange={(vendorId) => {
              // `undefined` clears the field; `""` reads as a *selected* value
              // that matches no option, so the Select renders an empty
              // selection (placeholder suppressed) until the refetch lands.
              form.setFieldValue("submitter", undefined);
              actions.updateFeatureCompanyParticipantWithEmptyUsers({
                vendorIds: [vendorId],
                companyParticipantTypeId:
                  CompanyParticipationType.RESPONSIBLE_CONTRACTOR,
                userParticipationTypeId: UserParticipationType.ASSIGNEE
              });
            }}
            showSearch
            filterOption
            optionFilterProp="label"
            disabled={disabled}
            data-testid="material-participants-responsible-contractor-select"
            notFoundContent={
              <SelectSearchNotFoundContent
                notFoundMsg={
                  responsibleContractorOptions?.length > 0
                    ? "Company not found. To add, go to "
                    : "No companies added to this project. To add, go to "
                }
                linkTitle="Project Companies"
                linkPath={`/project/${projectId}/settings/general/project-companies`}
                data-testid="material-participants-responsible-contractor-not-found"
              />
            }
          >
            {responsibleContractorOptions?.map((company: any) => {
              return (
                <Option
                  key={company.id}
                  value={company.id}
                  label={company.name}
                  data-testid="material-participants-responsible-contractor-option"
                >
                  {company.name}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
        <Tooltip
          title={
            !selectedRCCompanyId ? "First select a Responsible Contractor" : ""
          }
          placement="rightBottom"
          data-testid="material-participants-submitter-tooltip"
        >
          <Form.Item
            label={materialHeaderMap?.assignee}
            name="submitter"
            data-testid="material-participants-submitter-item"
          >
            <ProjectUserParticipantsDropdown
              userParticipationType={UserParticipationType.SUBMITTER}
              selectedCompanyId={selectedRCCompanyId}
              onChange={(userId: string) => {
                actions.updateFeatureUserParticipant({
                  user_id: userId,
                  user_participation_type_id: UserParticipationType.ASSIGNEE
                });
              }}
              notFoundContent="User not found. Go to Project Settings to add users"
              showSearch
              optionFilterProp="label"
              filterOption
              disabled={disabled || !selectedRCCompanyId}
              data-testid="material-participants-submitter-dropdown"
            />
          </Form.Item>
        </Tooltip>
        <Form.Item
          label={materialHeaderMap?.gc_representative}
          name="gc_representative"
          data-testid="material-participants-gc-representative-item"
        >
          <ProjectUserParticipantsDropdown
            userParticipationType={UserParticipationType.GC_REVIEWER}
            onChange={(userId: string) => {
              actions.updateFeatureUserParticipant({
                user_id: userId,
                user_participation_type_id:
                  UserParticipationType.GC_REPRESENTATIVE
              });
            }}
            notFoundContent="User not found. Go to Project Settings to add users"
            showSearch
            optionFilterProp="label"
            filterOption
            disabled={disabled}
            data-testid="material-participants-gc-representative-dropdown"
          />
        </Form.Item>
      </Form>
    </div>
  );
}
